import { useState, useRef, useEffect } from 'react';
import { useFormik } from 'formik';
import Drawer from '../Common/Drawer';
import { InputFormik, SearchableDropdown, Button, Dropdown } from '../Common';
import { ProjectRoleDto, ProjectAssigneesWithUserInfoDto, EmploymentType } from '../../commonTypes/project.types';
import { RateType } from '@cyberlabrs/helpers';
import { validationSchema } from './validationSchema';
import { getCurrencySymbol } from '../../util/general';

const BILLABLE_OPTIONS = [
    { label: 'Billable', value: 'billable' },
    { label: 'Non-billable', value: 'non-billable' },
];

const RATE_TYPE_OPTIONS = [
    { label: 'Hourly', value: RateType.HOURLY },
    { label: 'Fixed', value: RateType.FIXED },
];

const DEFAULT_RATE = '0';
const DEFAULT_EMPLOYMENT_TYPE = EmploymentType.FULL_TIME;
const DEFAULT_RATE_TYPE = RateType.HOURLY;
const DEFAULT_BILLABLE_TYPE = 'billable';

interface IMemberAssignment {
    memberId: string;
    role: string | number;
    billableType: string;
    rateType: string;
    employmentType: string;
    rate: string;
}

interface IAssignMembersDrawerProps {
    open: boolean;
    onClose: () => void;
    onAssign: (members: IMemberAssignment[]) => void;
    onUnassign?: () => void;
    availableMembers: { label: string; value: string; imageUrl?: string; jobTitle?: string }[];
    employmentTypes: { label: string; value: string }[];
    projectRoles: ProjectRoleDto[];
    editMode?: boolean;
    editMember?: ProjectAssigneesWithUserInfoDto;
    currency: string;
}

const AssignMembersDrawer = ({
    open,
    onClose,
    onAssign,
    onUnassign,
    availableMembers,
    employmentTypes,
    projectRoles,
    editMode = false,
    editMember,
    currency,
}: IAssignMembersDrawerProps) => {
    const formatRoleName = (name: string) => {
        return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
    };

    const roleOptions = projectRoles.map(role => ({ label: formatRoleName(role.name), value: role.id }));

    const getInitialMemberAssignments = (): IMemberAssignment[] => {
        if (editMode && editMember) {
            const isBillable = editMember.billable;
            return [
                {
                    memberId: editMember.userSub,
                    role: editMember.assignedRoles?.[0]?.projectRoleDto?.id?.toString() || '',
                    billableType: isBillable ? DEFAULT_BILLABLE_TYPE : 'non-billable',
                    rateType: (editMember as any).rateType || DEFAULT_RATE_TYPE,
                    employmentType: (editMember as any).employmentType || DEFAULT_EMPLOYMENT_TYPE,
                    rate: editMember.rate?.toString() || '',
                },
            ];
        }
        return [
            {
                memberId: '',
                role: '',
                billableType: DEFAULT_BILLABLE_TYPE,
                rateType: DEFAULT_RATE_TYPE,
                employmentType: DEFAULT_EMPLOYMENT_TYPE,
                rate: DEFAULT_RATE,
            },
        ];
    };

    const [memberAssignments, setMemberAssignments] = useState<IMemberAssignment[]>(getInitialMemberAssignments());
    const lastFormRef = useRef<HTMLDivElement>(null);

    const findRole = (roleId: string | number) => {
        return roleOptions.find(role => role.value === roleId) || { label: '', value: '' };
    };

    const findMember = (memberId: string) => {
        return availableMembers.find(member => member.value === memberId) || { label: '', value: '' };
    };

    const findEmploymentType = (typeId: string) => {
        return employmentTypes.find(type => type.value === typeId) || { label: '', value: '' };
    };

    const findBillableType = (billableTypeId: string) => {
        return BILLABLE_OPTIONS.find(type => type.value === billableTypeId) || { label: '', value: '' };
    };

    const findRateType = (rateTypeId: string) => {
        return RATE_TYPE_OPTIONS.find(type => type.value === rateTypeId) || { label: '', value: '' };
    };

    const context = useFormik({
        initialValues: { members: memberAssignments },
        validationSchema,
        enableReinitialize: true,
        onSubmit: values => {
            onAssign(values.members);
            handleClose();
        },
    });

    useEffect(() => {
        if (lastFormRef.current && memberAssignments.length > 1) {
            lastFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [memberAssignments.length]);

    useEffect(() => {
        if (open) {
            const initialAssignments = getInitialMemberAssignments();
            setMemberAssignments(initialAssignments);
            void context.setValues({ members: initialAssignments });
        }
    }, [open, editMode, editMember]);

    const handleClose = () => {
        setMemberAssignments([
            {
                memberId: '',
                role: '',
                billableType: DEFAULT_BILLABLE_TYPE,
                rateType: DEFAULT_RATE_TYPE,
                employmentType: DEFAULT_EMPLOYMENT_TYPE,
                rate: DEFAULT_RATE,
            },
        ]);
        context.resetForm();
        onClose();
    };

    const handleAddMoreMembers = () => {
        setMemberAssignments([
            ...memberAssignments,
            {
                memberId: '',
                role: '',
                billableType: DEFAULT_BILLABLE_TYPE,
                rateType: DEFAULT_RATE_TYPE,
                employmentType: DEFAULT_EMPLOYMENT_TYPE,
                rate: DEFAULT_RATE,
            },
        ]);
    };

    const handleRemoveMember = (index: number) => {
        const updatedMembers = memberAssignments.filter((_, i) => i !== index);
        setMemberAssignments(updatedMembers);
    };

    const updateMemberField = (index: number, field: keyof IMemberAssignment, value: string) => {
        const updatedMembers = [...memberAssignments];
        updatedMembers[index][field] = value;

        if (field === 'billableType' && value === 'non-billable') {
            updatedMembers[index].rateType = DEFAULT_RATE_TYPE;
            updatedMembers[index].employmentType = DEFAULT_EMPLOYMENT_TYPE;
            updatedMembers[index].rate = '';
        } else if (field === 'billableType' && value === 'billable') {
            if (!updatedMembers[index].rate || updatedMembers[index].rate.trim() === '') {
                updatedMembers[index].rate = DEFAULT_RATE;
            }
            if (!updatedMembers[index].employmentType || updatedMembers[index].employmentType.trim() === '') {
                updatedMembers[index].employmentType = DEFAULT_EMPLOYMENT_TYPE;
            }
            if (!updatedMembers[index].rateType) {
                updatedMembers[index].rateType = DEFAULT_RATE_TYPE;
            }
        }

        setMemberAssignments(updatedMembers);
        void context.setFieldValue('members', updatedMembers);
    };

    const getAvailableMembersForIndex = (currentIndex: number) => {
        const selectedMemberIds = memberAssignments
            .map((assignment, index) => (index !== currentIndex ? assignment.memberId : null))
            .filter(id => id !== null && id !== '');

        return availableMembers.filter(member => !selectedMemberIds.includes(member.value));
    };

    const isFormValid = () => {
        return memberAssignments.every(member => {
            const baseValid = member.memberId && member.role && member.billableType;
            if (member.billableType === 'non-billable') {
                return baseValid;
            }
            return baseValid && member.rateType && member.employmentType && member.rate.trim();
        });
    };

    return (
        <Drawer
            open={open}
            title={editMode ? 'Edit assignee' : 'Assign Member'}
            onClose={handleClose}
            showFooter={true}
            className="assign-members-drawer"
            leftButtons={
                editMode && onUnassign
                    ? [
                          {
                              text: 'Unassign',
                              variant: 'text',
                              onClick: onUnassign,
                          },
                      ]
                    : undefined
            }
            rightButtons={[
                {
                    text: 'Cancel',
                    variant: 'text',
                    onClick: handleClose,
                },
                {
                    text: editMode ? 'Save' : 'Assign Member',
                    variant: 'contained',
                    onClick: () => context.handleSubmit(),
                    disabled: !isFormValid(),
                },
            ]}
        >
            <div className="assign-members-content">
                {editMode && editMember && (
                    <div className="edit-member__info">
                        <img
                            src={editMember.imageUrl || '/svg/person.svg'}
                            alt={`${editMember.givenName} ${editMember.familyName}`}
                            className="edit-member__avatar"
                            onError={e => ((e.target as HTMLImageElement).src = '/svg/person.svg')}
                        />
                        <div className="edit-member__name">
                            {`${editMember.givenName || ''} ${editMember.familyName || ''}`.trim() || 'Unknown Member'}
                        </div>
                    </div>
                )}
                {memberAssignments.map((member, index) => (
                    <div key={index} className="member-assignment-form" ref={index === memberAssignments.length - 1 ? lastFormRef : null}>
                        {!editMode && (
                            <SearchableDropdown
                                label="Select Member"
                                menuItems={getAvailableMembersForIndex(index)}
                                selected={findMember(member.memberId)}
                                onChange={item => updateMemberField(index, 'memberId', item.value as string)}
                                isRequired
                                showImage
                            />
                        )}
                        {!editMode && (
                            <Dropdown
                                label="Role"
                                menuItems={roleOptions}
                                selected={findRole(member.role)}
                                onChange={item => updateMemberField(index, 'role', item.value as string)}
                            />
                        )}
                        <Dropdown
                            label="Billable Type"
                            menuItems={BILLABLE_OPTIONS}
                            selected={findBillableType(member.billableType)}
                            onChange={item => updateMemberField(index, 'billableType', item.value as string)}
                        />

                        {member.billableType === 'billable' && (
                            <Dropdown
                                label="Rate Type"
                                menuItems={RATE_TYPE_OPTIONS}
                                selected={findRateType(member.rateType)}
                                onChange={item => updateMemberField(index, 'rateType', item.value as string)}
                            />
                        )}

                        <SearchableDropdown
                            label="Select Employment Type"
                            menuItems={employmentTypes}
                            selected={findEmploymentType(member.employmentType)}
                            onChange={item => updateMemberField(index, 'employmentType', item.value as string)}
                            isRequired
                            disabled={member.billableType === 'non-billable'}
                            className={member.employmentType === DEFAULT_EMPLOYMENT_TYPE ? 'dropdown--placeholder-value' : ''}
                        />

                        <InputFormik
                            context={context}
                            id={`members.${index}.rate`}
                            label={`Rate (${getCurrencySymbol(currency)})`}
                            placeholder="Enter Hourly Rate"
                            value={member.rate}
                            onChange={e => updateMemberField(index, 'rate', e.target.value)}
                            isRequired
                            isDisabled={member.billableType === 'non-billable'}
                            className={member.rate === DEFAULT_RATE ? 'input-style--placeholder-value' : ''}
                        />

                        {memberAssignments.length > 1 && (
                            <button type="button" className="remove-member-btn" onClick={() => handleRemoveMember(index)}>
                                X
                            </button>
                        )}
                    </div>
                ))}

                {!editMode && (
                    <Button text="Add More Members" variant="text" iconSrc="/svg/plus-blue-without-circle.svg" onClick={handleAddMoreMembers} />
                )}
            </div>
        </Drawer>
    );
};

export default AssignMembersDrawer;
